import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FeatureFlagsDirective } from '@core/feature-flag/feature-flags.directive';
import { TranslateUiModule } from '@core/translation/translate-ui.module';
import { AuthService } from '@features/auth/auth.service';
import { UserRoles } from '@features/auth/user-roles';
import {
  MittanbudEnum,
  MittanbudEvent,
  MittanbudKey,
  MittanbudType
} from '@features/scheduler/unscheduled-projects/mittanbud-banner/mittanbud-enum';
import { MittanbudPopUpComponent } from '@features/scheduler/unscheduled-projects/mittanbud-banner/mittanbud-pop-up/mittanbud-pop-up.component';
import { MittanbudService } from '@features/scheduler/unscheduled-projects/mittanbud-banner/mittanbud.service';
import { SegmentService } from '@services/common/segment.service';
import { CountriesIdEnum } from '@shared/enums/countries.enum';
import { ButtonsModule } from '@ui-kit/buttons/buttons.module';
import { forkJoin } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { NgOptimizedImage } from '@angular/common';

@Component({
  selector: 'mittanbud-banner',
  standalone: true,
  imports: [FeatureFlagsDirective, ButtonsModule, TranslateUiModule, NgOptimizedImage],
  templateUrl: './mittanbud-banner.component.html',
  styleUrl: './mittanbud-banner.component.scss',
  host: {
    class: 'flex w-full flex-wrap rounded-sm'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MittanbudBannerComponent implements OnInit {
  private _auth: AuthService = inject(AuthService);
  private _segmentService: SegmentService = inject(SegmentService);
  private _matDialog: MatDialog = inject(MatDialog);
  private _mittanbudService: MittanbudService = inject(MittanbudService);

  @Input({ required: true }) view: string;

  public MittanbudType = MittanbudType;
  public mittanbudEnum = MittanbudEnum;
  winMoreJobs = marker('Win more jobs!');
  mittanbudLabel = marker('Mittanbud!');
  claimOffer = marker('Claim offer');
  mainText = marker('You\'re on a roll! Get 50% off 10 Mittanbud tickets and grow your client list.');
  mainSubtext = marker('Get 50 % off 10 Mittanbud tickets and grow your client list.');

  get dialog() {
    return this._matDialog;
  }
  get showMittanbud() {
    const countryId = this._auth.user?.account.country_id;
    const userRole: UserRoles = this._auth.user?.role;

    return (
      !this.mittanbud.closedByAccount() &&
      countryId === CountriesIdEnum.Norway &&
      userRole === UserRoles.Admin &&
      this.mittanbud.closedByUser()
    );
  }

  get mittanbud() {
    return this._mittanbudService;
  }

  open(type: string): void {
    this.dialog.open(MittanbudPopUpComponent, {
      width: '545px',
      panelClass: 'mittanbud-pop-up'
    });

    this.trackEvents(type);
  }

  trackEvents(type: string): void {
    switch (type) {
      case MittanbudType.ON_SCHEDULER: {
        this.segmentEvent(MittanbudEvent.SCHEDULER);
        break;
      }

      case MittanbudType.ON_PROJECT: {
        this.segmentEvent(MittanbudEvent.PROJECT);
        break;
      }
    }
  }

  cancelBanner(ev?): void {
    this.mittanbud.closedByUser.set(false);
    if (ev) {
      ev.stopPropagation();
      ev.preventDefault();
    }

    this.mittanbud
      .closeByUser({
        value: JSON.stringify({
          show_mittanbud_banner: {
            value: this.mittanbud.closedByUser()
          }
        })
      })
      .subscribe();
  }

  segmentEvent(type: string): void {
    const eventName: string = 'Mittanbud_banner';
    switch (type) {
      case MittanbudEvent.SCHEDULER: {
        this._segmentService.track(eventName, {
          banner: type
        });
        break;
      }
      case MittanbudEvent.PROJECT: {
        this._segmentService.track(eventName, {
          banner: type
        });
        break;
      }
    }
  }

  fetchPermissions(): void {
    forkJoin({
      accountPermission: this.mittanbud.getAccountPermission(this._auth.isAdmin()),
      userPermission: this.mittanbud.getUserPermission()
    })
      .pipe(
        filter(({ accountPermission, userPermission }) => !!accountPermission && !!userPermission),
        tap(({ accountPermission, userPermission }): void => {
          this.mittanbud.closedByAccount.set(accountPermission[MittanbudKey.MITTANBUD_ACCOUNT_KEY]);
          this.mittanbud.closedByUser.set(userPermission[MittanbudKey.MITTANBUD_USER_KEY]);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.fetchPermissions();
  }

  constructor() {}
}
